// PHASE_4 / B1-frontend — Görevlerim (me-tasks, employee_self).
// Faz 8.6 / Batch A7 restyles the VISUAL layer to the novem my-tasks language
// (NovemHR-ekranlar emp-tasks.jsx EmpTasks port: serif PageHeader+em, a 4-up KPI
// StatCard strip, a novem status FilterPillBar with count badges replacing the
// Radix Tabs).
//
// Lists the caller's OWN tasks via GET /v1/tasks?mine=true. Deriving "mine"
// from the tier alone was wrong above employee_self: this screen showed the GM
// four tasks of which three were Tolga Kurt's (SEM-6054). The status pills
// narrow SERVER-side via the `status` query param so a tab change refetches a
// fresh keyset page.
//
// The KPI strip and pill counts read GET /v1/tasks/summary, never the loaded
// list: the list holds one status filter and one keyset page, so counting it
// showed e.g. "0 completed" on the open tab while completed tasks existed.
//
// /me/tasks/:taskId opens the detail drawer over the list, so a task (from
// global search or a notification) can be deep-linked. A task outside the
// loaded page is fetched on its own.
//
// Pure, shell-agnostic Content (ADR-014 §2). No money here.

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { AlarmClock, CircleCheckBig, Inbox, ListTodo } from "lucide-react";

import {
  FilterPillBar,
  PageHeader,
  StatCard,
  type FilterPillItem,
} from "@novemai/components";

import { EnvelopeError } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTabParam } from "@/lib/use-tab-param";
import { fetchMyTaskSummary, fetchTask, fetchTasksPage, type TaskRow } from "./api";
import { TasksTable } from "./tasks-table";

/** Tab id → the server `status` filter it sends (`all` sends none). */
const TAB_STATUS: Readonly<Record<string, string | undefined>> = {
  open: "open",
  snoozed: "snoozed",
  completed: "completed",
  all: undefined,
};

const SUMMARY_KEY = ["tasks", "mine", "summary"] as const;

export function MyTasksContent(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams<{ taskId?: string }>();
  const [tab, setTab] = useTabParam("tab", Object.keys(TAB_STATUS), "open");
  const status = TAB_STATUS[tab];

  const query = useKeysetQuery<TaskRow>({
    queryKey: ["tasks", "mine", tab],
    fetchPage: (cursor) =>
      fetchTasksPage(cursor, status !== undefined ? { status, mine: true } : { mine: true }),
  });
  const summary = useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: ({ signal }) => fetchMyTaskSummary(signal),
  });

  const loadedDetail = taskId === undefined
    ? undefined
    : query.items.find((row) => row.task_id === taskId);
  const fetchedDetail = useQuery({
    queryKey: ["tasks", "detail", taskId],
    queryFn: () => fetchTask(taskId ?? ""),
    enabled: taskId !== undefined && loadedDetail === undefined && !query.isLoading,
    retry: false,
  });
  const detailTask = taskId === undefined ? null : (loadedDetail ?? fetchedDetail.data ?? null);

  const counts = summary.data;
  const none = t("tasks.detail_none");
  const total = counts === undefined
    ? undefined
    : Object.values(counts).reduce((sum, n) => sum + n, 0);
  const kpi = (n: number | undefined): string => (n === undefined ? none : String(n));

  // exactOptionalPropertyTypes wants the count key omitted, not undefined,
  // while the summary is still loading or failed.
  const pill = (value: string, label: string, count: number | undefined): FilterPillItem => ({
    value,
    label,
    ...(count !== undefined ? { count } : {}),
  });
  const filterItems: ReadonlyArray<FilterPillItem> = [
    pill("open", t("tasks.tab_open"), counts?.open),
    pill("snoozed", t("tasks.tab_snoozed"), counts?.snoozed),
    pill("completed", t("tasks.tab_completed"), counts?.completed),
    pill("all", t("tasks.tab_all"), total),
  ];

  const refetchAll = (): void => {
    query.refetch();
    void summary.refetch();
  };

  const openDetail = (task: TaskRow | null): void => {
    navigate({
      pathname: task === null ? "/me/tasks" : `/me/tasks/${encodeURIComponent(task.task_id)}`,
      search: location.search,
    });
  };

  const detailProblem = taskId !== undefined && loadedDetail === undefined && fetchedDetail.isError
    ? fetchedDetail.error instanceof EnvelopeError
      && (fetchedDetail.error.httpStatus === 404 || fetchedDetail.error.httpStatus === 403)
      ? t("tasks.detail_not_found")
      : t("tasks.detail_load_error")
    : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title={
          <>
            {t("tasks.my_title_lead")} <em>{t("tasks.my_title_em")}</em>
          </>
        }
        description={t("tasks.my_description")}
      />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("tasks.my_kpi_open")}
          value={kpi(counts?.open)}
          tone="accent"
          icon={<Inbox size={18} />}
        />
        <StatCard
          label={t("tasks.my_kpi_snoozed")}
          value={kpi(counts?.snoozed)}
          tone="warning"
          icon={<AlarmClock size={18} />}
        />
        <StatCard
          label={t("tasks.my_kpi_completed")}
          value={kpi(counts?.completed)}
          tone="success"
          icon={<CircleCheckBig size={18} />}
        />
        <StatCard
          label={t("tasks.my_kpi_all")}
          value={kpi(total)}
          tone="info"
          icon={<ListTodo size={18} />}
        />
      </div>

      <FilterPillBar
        aria-label={t("tasks.my_title")}
        items={filterItems}
        value={tab}
        onChange={setTab}
      />

      {detailProblem !== null && (
        <p role="alert" className="text-sm text-[var(--color-error)]">{detailProblem}</p>
      )}

      <TasksTable
        query={{ ...query, refetch: refetchAll }}
        caption={t("tasks.my_caption")}
        emptyTitle={t("tasks.my_empty_title")}
        emptyDescription={t("tasks.my_empty_description")}
        detail={{ task: detailTask, onChange: openDetail }}
      />
    </div>
  );
}
